import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { PageLoading } from '@/components/common/PageStates'
import { DocumentWizardShell } from '@/components/rfid/DocumentWizardShell'
import { TransferContextCard } from '@/components/rfid/TransferContextCard'
import { WizardNavFooter } from '@/components/rfid/WizardNavFooter'
import { getMovement } from '@/config/movements'
import {
  EWAY_OPTIONS,
  POD_OPTIONS,
  REASON_OPTIONS,
  TRANSPORT_BY_LIST,
  TRANSPORT_MODES,
  VEHICLE_TYPES,
} from '@/config/transportUdf'
import { useStoreHydration } from '@/hooks/useStoreHydration'
import { useDocumentWizardStore } from '@/stores/documentWizardStore'
import type { TransferDetails, TransportDetails } from '@/types/wizard'
import { customerCodeFromDocument } from '@/utils/customerDetails'
import {
  type UdfFieldError,
  validateMovementCapture,
} from '@/utils/transportUdfValidation'
import { sanitizeDriverMobile } from '@/utils/transportFieldUtils'

const UDF_MSG = {
  vehicleNoRequired: 'Vehicle number is required',
  transporterNameRequired: 'Transporter name is required',
  driverNameRequired: 'Driver name is required',
  driverMobileRequired: 'Driver mobile is required',
  driverMobileTenDigits: 'Driver mobile must be 10 digits',
  lrRequired: 'LR number is required',
  freightRequired: 'Freight is required',
  transportationByRequired: 'Transportation by is required',
  vehicleTypeRequired: 'Vehicle type is required',
  transportModeRequired: 'Transport mode is required',
  distanceRequired: 'Distance is required',
  reasonRequired: 'Reason is required',
  containerSealRequired: 'Container seal is required',
  ewayRequired: 'E-way type is required',
  podRequired: 'POD received is required',
}

function labeledOptions(list: { value: string; label: string }[]) {
  return list.map((o) => ({ value: o.value, label: `${o.value} : ${o.label}` }))
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-[11px] font-semibold text-red-500">{message}</p>
}

function backPath(movementId: string | null): string {
  if (!movementId) return '/'
  try {
    const m = getMovement(movementId as import('@/config/movements').MovementId)
    if (m.listBacked) return `/movements/${m.id}/documents`
    if (m.id === 'transfer_out' || m.id === 'transfer_in') {
      return `/transfer/customer?movement=${m.id}`
    }
  } catch {
    /* ignore */
  }
  return '/'
}

export default function WizardCapturePage() {
  const navigate = useNavigate()
  const hydrated = useStoreHydration(useDocumentWizardStore.persist)
  const document = useDocumentWizardStore((s) => s.document)
  const movementId = useDocumentWizardStore((s) => s.movementId)
  const transport = useDocumentWizardStore((s) => s.transport)
  const effectiveDate = useDocumentWizardStore((s) => s.effectiveDate)
  const remarks = useDocumentWizardStore((s) => s.remarks)
  const transferDetails = useDocumentWizardStore((s) => s.transferDetails)
  const transferBpCode = useDocumentWizardStore((s) => s.transferBpCode)
  const transferCustomerName = useDocumentWizardStore((s) => s.transferCustomerName)
  const transferToBpCode = useDocumentWizardStore((s) => s.transferToBpCode)
  const transferToCustomerName = useDocumentWizardStore((s) => s.transferToCustomerName)
  const transferBaseDocNo = useDocumentWizardStore((s) => s.transferBaseDocNo)
  const customerDetailsError = useDocumentWizardStore((s) => s.customerDetailsError)
  const setTransport = useDocumentWizardStore((s) => s.setTransport)
  const setTransferDetails = useDocumentWizardStore((s) => s.setTransferDetails)
  const setEffectiveDate = useDocumentWizardStore((s) => s.setEffectiveDate)
  const setRemarks = useDocumentWizardStore((s) => s.setRemarks)
  const loadCustomerPostingContext = useDocumentWizardStore((s) => s.loadCustomerPostingContext)

  const [udfErrors, setUdfErrors] = useState<UdfFieldError[]>([])

  const movement = movementId ? getMovement(movementId) : null
  const isTransferOut = movement?.wizardKind === 'transferOut'
  const isTransferIn = movement?.wizardKind === 'transferIn'
  const isTransfer = isTransferOut || isTransferIn
  const showTransportUdf =
    movement?.wizardKind === 'standard' || movement?.wizardKind === 'relocation'

  useEffect(() => {
    if (!hydrated || !document || !movementId) return
    const code =
      transferBpCode || customerCodeFromDocument(document, movementId)
    void loadCustomerPostingContext(code)
  }, [hydrated, document, movementId, transferBpCode, loadCustomerPostingContext])

  const errMap = useMemo(() => {
    const m = new Map<string, string>()
    udfErrors.forEach((e) => m.set(e.field, e.message))
    return m
  }, [udfErrors])

  if (!hydrated) {
    return <PageLoading tip="Restoring scan session…" />
  }

  if (!document || !movementId || !movement) {
    return <Navigate to="/" replace />
  }

  const patchTransport = (patch: Partial<TransportDetails>) => {
    const next = { ...transport, ...patch }
    setTransport(patch)
    if (udfErrors.length > 0) {
      setUdfErrors(validateMovementCapture(movement, next, UDF_MSG))
    }
  }

  const onContinue = () => {
    if (isTransferOut && !String(transferDetails.transporterName ?? '').trim()) {
      toast.error(UDF_MSG.transporterNameRequired)
      return
    }
    if (showTransportUdf) {
      const errors = validateMovementCapture(movement, transport, UDF_MSG)
      if (errors.length) {
        setUdfErrors(errors)
        toast.error(errors[0]?.message ?? 'Please complete transport details')
        return
      }
    }
    setUdfErrors([])
    navigate('/wizard/scan')
  }

  const goBack = () => navigate(backPath(movementId))

  return (
    <DocumentWizardShell
      step={1}
      title={`${movement.legacyName} — Input`}
      subtitle="Step 1 of 4"
      onBack={goBack}
      document={document}
      customerNumber={isTransfer ? transferBpCode : undefined}
      footer={
        <WizardNavFooter
          onBack={goBack}
          onNext={onContinue}
          nextLabel="Continue to scan"
        />
      }
    >
      {customerDetailsError ? (
        <div className="mb-3 rounded-[10px] border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] font-semibold text-amber-800">
          <div className="font-extrabold">Customer details</div>
          <div className="mt-0.5 font-semibold opacity-90">{customerDetailsError}</div>
        </div>
      ) : null}

      {isTransfer ? (
        <TransferContextCard
          fromLabel={transferCustomerName || '—'}
          fromCode={transferBpCode}
          toLabel={isTransferOut ? transferToCustomerName || undefined : undefined}
          toCode={isTransferOut ? transferToBpCode || undefined : undefined}
          refDoc={isTransferIn ? transferBaseDocNo || undefined : undefined}
        />
      ) : null}

      <div className="rfid-section-label">Posting</div>

      <div className="rfid-form-grid">
        <div className="rfid-field">
          <label className="rfid-field__label" htmlFor="effective-date">
            Effective / posting date
          </label>
          <input
            id="effective-date"
            type="date"
            className="rfid-field__control"
            value={dayjs(effectiveDate).format('YYYY-MM-DD')}
            onChange={(e) => {
              if (e.target.value) setEffectiveDate(dayjs(e.target.value).toDate())
            }}
          />
        </div>

        <div className="rfid-field rfid-form-grid__span-2">
          <label className="rfid-field__label" htmlFor="remarks">
            Remarks
          </label>
          <textarea
            id="remarks"
            rows={2}
            className="rfid-field__control"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Optional remarks"
          />
        </div>
      </div>

      {isTransferOut ? (
        <>
          <div className="rfid-section-label mt-4">Transfer details</div>
          <div className="rfid-form-grid">
            {(
              [
                ['vehicleNo', 'Vehicle number'],
                ['transporterName', 'Transporter name *'],
                ['invoiceNo', 'Invoice number'],
                ['lrNo', 'LR number'],
                ['location', 'Location'],
              ] as const
            ).map(([key, label]) => (
              <div className="rfid-field" key={key}>
                <label className="rfid-field__label" htmlFor={`transfer-${key}`}>
                  {label}
                </label>
                <input
                  id={`transfer-${key}`}
                  className="rfid-field__control"
                  value={transferDetails[key]}
                  onChange={(e) =>
                    setTransferDetails({ [key]: e.target.value } as Partial<TransferDetails>)
                  }
                />
              </div>
            ))}
          </div>
        </>
      ) : null}

      {isTransferIn ? (
        <p className="mb-3 text-[11px] font-semibold leading-4 text-muted-foreground">
          Confirm the posting date, then continue to scan tags for this base document.
        </p>
      ) : null}

      {showTransportUdf ? (
        <>
          <div className="rfid-section-label mt-4">Transport details</div>

          <div className="rfid-form-grid">
          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="vehicle-no">
              Vehicle no
            </label>
            <input
              id="vehicle-no"
              className="rfid-field__control"
              value={transport.VEHICLE_NO}
              onChange={(e) => patchTransport({ VEHICLE_NO: e.target.value })}
            />
            <FieldError message={errMap.get('VEHICLE_NO')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="vehicle-type">
              Vehicle type
            </label>
            <select
              id="vehicle-type"
              className="rfid-field__control"
              value={transport.VEHICLE_TYPE || ''}
              onChange={(e) => patchTransport({ VEHICLE_TYPE: e.target.value })}
            >
              <option value="">Select</option>
              {labeledOptions(VEHICLE_TYPES).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <FieldError message={errMap.get('VEHICLE_TYPE')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="transport-mode">
              Transport mode
            </label>
            <select
              id="transport-mode"
              className="rfid-field__control"
              value={transport.TRANSPORT_MODE || ''}
              onChange={(e) => patchTransport({ TRANSPORT_MODE: e.target.value })}
            >
              <option value="">Select</option>
              {labeledOptions(TRANSPORT_MODES).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <FieldError message={errMap.get('TRANSPORT_MODE')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="transportation-by">
              Transportation by
            </label>
            <select
              id="transportation-by"
              className="rfid-field__control"
              value={transport.TRANPORTATION_BY || ''}
              onChange={(e) => patchTransport({ TRANPORTATION_BY: e.target.value })}
            >
              <option value="">Select</option>
              {labeledOptions(TRANSPORT_BY_LIST).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <FieldError message={errMap.get('TRANPORTATION_BY')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="transporter-code">
              Transporter code
            </label>
            <input
              id="transporter-code"
              className="rfid-field__control"
              value={transport.TRANSPORTER_CODE}
              onChange={(e) => patchTransport({ TRANSPORTER_CODE: e.target.value })}
            />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="transporter-name">
              Transporter name *
            </label>
            <input
              id="transporter-name"
              className="rfid-field__control"
              value={transport.TRANSPORTER_NAME}
              onChange={(e) => patchTransport({ TRANSPORTER_NAME: e.target.value })}
            />
            <FieldError message={errMap.get('TRANSPORTER_NAME')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="driver-name">
              Driver name
            </label>
            <input
              id="driver-name"
              className="rfid-field__control"
              value={transport.DRIVER_NAME}
              onChange={(e) => patchTransport({ DRIVER_NAME: e.target.value })}
            />
            <FieldError message={errMap.get('DRIVER_NAME')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="driver-mobile">
              Driver mobile
            </label>
            <input
              id="driver-mobile"
              className="rfid-field__control"
              inputMode="numeric"
              maxLength={10}
              value={transport.DRIVER_MOB_NO}
              onChange={(e) =>
                patchTransport({
                  DRIVER_MOB_NO: sanitizeDriverMobile(
                    e.target.value.replace(/\D/g, '').slice(0, 10)
                  ),
                })
              }
            />
            <FieldError message={errMap.get('DRIVER_MOB_NO')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="lr-no">
              LR no
            </label>
            <input
              id="lr-no"
              className="rfid-field__control"
              value={transport.LR_NO}
              onChange={(e) => patchTransport({ LR_NO: e.target.value })}
            />
            <FieldError message={errMap.get('LR_NO')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="inspected-by">
              Inspected by
            </label>
            <input
              id="inspected-by"
              className="rfid-field__control"
              value={transport.INSPECTEDBY}
              onChange={(e) => patchTransport({ INSPECTEDBY: e.target.value })}
            />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="freight">
              Freight
            </label>
            <input
              id="freight"
              className="rfid-field__control"
              value={transport.freight}
              onChange={(e) => patchTransport({ freight: e.target.value })}
            />
            <FieldError message={errMap.get('freight')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="distance">
              Distance
            </label>
            <input
              id="distance"
              className="rfid-field__control"
              value={transport.distance}
              onChange={(e) => patchTransport({ distance: e.target.value })}
            />
            <FieldError message={errMap.get('distance')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="reason">
              Reason
            </label>
            <select
              id="reason"
              className="rfid-field__control"
              value={transport.reason || ''}
              onChange={(e) => patchTransport({ reason: e.target.value })}
            >
              <option value="">Select</option>
              {REASON_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <FieldError message={errMap.get('reason')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="container-seal">
              Container seal
            </label>
            <input
              id="container-seal"
              className="rfid-field__control"
              value={transport.containerSeal}
              onChange={(e) => patchTransport({ containerSeal: e.target.value })}
            />
            <FieldError message={errMap.get('containerSeal')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="eway">
              E-way type
            </label>
            <select
              id="eway"
              className="rfid-field__control"
              value={transport.eway || ''}
              onChange={(e) => patchTransport({ eway: e.target.value })}
            >
              <option value="">Select</option>
              {EWAY_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <FieldError message={errMap.get('eway')} />
          </div>

          <div className="rfid-field">
            <label className="rfid-field__label" htmlFor="pod">
              POD received
            </label>
            <select
              id="pod"
              className="rfid-field__control"
              value={transport.pod || ''}
              onChange={(e) => patchTransport({ pod: e.target.value })}
            >
              <option value="">Select</option>
              {POD_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <FieldError message={errMap.get('pod')} />
          </div>
          </div>
        </>
      ) : null}
    </DocumentWizardShell>
  )
}
